/**
 * The council lookup, ported from the static build's Lookup.jsx and lookup()
 * in content.js. Starts every [data-awdd-lookup] that
 * template-parts/lookup/lookup.php renders, and raises the entry dialog on a
 * first visit to the front page.
 *
 * Four outcomes, each drawn from a <template> the PHP renders with its copy:
 *   single    name the council and ask before moving
 *   multiple  a boundary postcode: show only the matches and ask
 *   none      outside the program: say so and take the notify form
 *   invalid   an inline message; the field and the list stay available
 *
 * Program councils match on their own postcodes (window.awddCouncils). The
 * suburb index and the postcode to council map are fetched once, on the first
 * focus or submit of any lookup. If that fetch fails, postcodes still match the
 * program councils, a suburb reads as invalid, and nothing throws.
 *
 * No copy lives here: every string comes from the templates.
 */

import { choose, current, dismiss, isDismissed } from './council.js';
import { openDialog } from './dialog.js';

const ENTRY_DELAY = 700;

const councils = () => (Array.isArray(window.awddCouncils) ? window.awddCouncils : []);
const bySlug = (slug) => councils().find((c) => c.slug === slug) || null;

/* ---------- Reference data ---------- */

const EMPTY = { suburbs: {}, postcodes: {} };
let dataPromise = null;

const getJson = (url) => fetch(url, { credentials: 'same-origin' }).then((r) => {
	if (!r.ok) throw new Error(r.status);
	return r.json();
});

function loadData() {
	if (dataPromise) return dataPromise;
	// Versioned URLs, printed by functions.php.
	const urls = window.awddLookupData || {};
	if (!urls.suburbs || !urls.postcodes || typeof fetch !== 'function') {
		dataPromise = Promise.resolve(EMPTY);
		return dataPromise;
	}
	dataPromise = Promise.all([getJson(urls.suburbs), getJson(urls.postcodes)])
		.then(([suburbs, postcodes]) => ({
			suburbs: suburbs && typeof suburbs === 'object' ? suburbs : {},
			postcodes: postcodes && typeof postcodes === 'object' ? postcodes : {},
		}))
		.catch(() => {
			// Try again on the next search rather than holding the failure.
			dataPromise = null;
			return EMPTY;
		});
	return dataPromise;
}

/* ---------- The mapping, as the static lookup() ---------- */

// Copied verbatim from normalise() in the static build's content.js, the same
// function bin/build-lookup-data.mjs keyed suburbs.json with.
const normalise = (raw) =>
	String(raw || '')
		.toLowerCase()
		.replace(/\s*,?\s*(vic|victoria)\s*\d*\s*$/, '')
		.replace(/[^a-z0-9 -]/g, '')
		.replace(/\s+/g, ' ')
		.trim();

function lookup(raw, data) {
	const q = normalise(raw);
	if (!q) return { outcome: 'empty', matches: [] };
	const asSuburb = Object.prototype.hasOwnProperty.call(data.suburbs, q) ? data.suburbs[q] : null;
	const pc = asSuburb || q;
	if (!/^\d{4}$/.test(pc)) return { outcome: 'invalid', matches: [] };
	const matches = councils().filter((c) => Array.isArray(c.postcodes) && c.postcodes.indexOf(pc) !== -1);
	// The councils this postcode also falls in that are not in the program, on
	// every outcome rather than only on the miss.
	const program = data.postcodes.__program || {};
	const matchedLgas = matches.map((c) => program[c.slug]).filter(Boolean);
	const listed = Array.isArray(data.postcodes[pc]) ? data.postcodes[pc] : [];
	const outside = listed.filter((n) => matchedLgas.indexOf(n) === -1);
	if (matches.length === 1) return { outcome: 'single', matches, postcode: pc, outside };
	if (matches.length > 1) return { outcome: 'multiple', matches, postcode: pc, outside };
	return { outcome: 'none', matches: [], postcode: pc, outside };
}

/* ---------- Template filling ---------- */

const TOKEN = /\{(council|councils|postcode|count)\}/g;

function fillTokens(root, tokens) {
	const swap = (s) => s.replace(TOKEN, (m, k) => (tokens[k] !== undefined ? String(tokens[k]) : m));
	const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
	for (let n = walker.nextNode(); n; n = walker.nextNode()) {
		if (n.nodeValue.indexOf('{') !== -1) n.nodeValue = swap(n.nodeValue);
	}
	root.querySelectorAll('*').forEach((el) => {
		Array.from(el.attributes).forEach((a) => {
			if (a.value.indexOf('{') !== -1) a.value = swap(a.value);
		});
	});
}

// Keep or drop every [data-awdd-when] by its condition.
function applyConditions(root, conditions) {
	root.querySelectorAll('[data-awdd-when]').forEach((el) => {
		if (!conditions[el.dataset.awddWhen]) el.remove();
		else el.removeAttribute('data-awdd-when');
	});
}

// Repeat a [data-awdd-each] element's first child once per item.
function repeat(root, name, items, each) {
	root.querySelectorAll(`[data-awdd-each="${name}"]`).forEach((list) => {
		const row = list.firstElementChild;
		if (!row) return;
		row.remove();
		items.forEach((item) => {
			const clone = row.cloneNode(true);
			each(clone, item);
			list.appendChild(clone);
		});
	});
}

/* ---------- The notify form ---------- */

const holder = () => document.querySelector('[data-awdd-notify-holder]');
const notifyId = () => {
	const h = holder();
	return h ? parseInt(h.dataset.awddNotifyHolder, 10) || 0 : 0;
};
const notifyWrapper = () => {
	const id = notifyId();
	if (!id) return null;
	return document.getElementById(`gform_wrapper_${id}`) || document.getElementById(`gform_confirmation_wrapper_${id}`);
};

// The lookup result currently holding the form, so a Gravity Forms re-render
// can be filled again.
let notifyOwner = null;

// The council select stands in for the form's own council field, whose label
// and input are hidden; its validation message stays. Field 2 is the postcode.
function syncNotify() {
	if (!notifyOwner) return;
	const id = notifyId();
	const { select, postcode } = notifyOwner;
	const council = document.getElementById(`input_${id}_1`);
	const pc = document.getElementById(`input_${id}_2`);
	const field = document.getElementById(`field_${id}_1`);
	if (field) {
		// The whole field goes until it has an error to show, so it adds no gap.
		const error = field.classList.contains('gfield_error');
		field.hidden = !error;
		field.querySelectorAll('.gfield_label, .ginput_container').forEach((el) => { el.hidden = true; });
		if (select) {
			if (error) select.setAttribute('aria-invalid', 'true');
			else select.removeAttribute('aria-invalid');
		}
	}
	if (pc) pc.value = postcode || '';
	if (council && select) {
		const opt = select.options[select.selectedIndex];
		council.value = opt && opt.value ? opt.text.trim() : '';
	}
	// A confirmation has replaced the form: the council question is answered.
	const confirmation = document.getElementById(`gform_confirmation_wrapper_${id}`);
	if (confirmation && notifyOwner.slot.contains(confirmation)) {
		const label = select && select.closest('[data-awdd-notify-council]');
		if (label) label.hidden = true;
	}
}

function returnNotify(slot) {
	const wrapper = notifyWrapper();
	const h = holder();
	if (wrapper && h && slot && slot.contains(wrapper)) h.prepend(wrapper);
	if (notifyOwner && notifyOwner.slot === slot) notifyOwner = null;
}

/* ---------- One lookup ---------- */

function initLookup(root) {
	const form = root.querySelector('[data-awdd-lookup-form]');
	const input = root.querySelector('[data-awdd-lookup-input]');
	const result = root.querySelector('[data-awdd-lookup-result]');
	const tpl = (name) => root.querySelector(`template[data-outcome="${name}"]`);
	if (!form || !input || !result) return;

	let last = null;

	const go = (council) => {
		if (!council) return;
		choose(council.slug);
		window.location.assign(council.url);
	};

	const clear = () => {
		const slot = result.querySelector('[data-awdd-notify-slot]');
		if (slot) returnNotify(slot);
		result.replaceChildren();
		input.removeAttribute('aria-invalid');
		input.removeAttribute('aria-describedby');
		last = null;
	};

	const render = (res, picked) => {
		clear();
		last = res;
		const t = tpl(res.outcome);
		if (!t) return;
		const frag = t.content.cloneNode(true);
		const outside = res.outside || [];
		const wrapper = notifyWrapper();
		const match = res.matches[0] || null;

		applyConditions(frag, {
			logo: Boolean(match && match.logo),
			postcode: Boolean(res.postcode),
			'no-postcode': !res.postcode,
			outside: outside.length > 0,
			'outside-one': outside.length === 1,
			'outside-many': outside.length > 1,
			'outside-none': outside.length === 0,
			notify: Boolean(wrapper),
		});

		repeat(frag, 'matches', res.matches, (row, c) => {
			fillTokens(row, { council: c.name });
			if (row.matches('[data-awdd-go]')) row.dataset.awddGo = c.slug;
		});
		repeat(frag, 'outside', outside, (row, name) => {
			fillTokens(row, { council: name });
			if (row.matches('[data-awdd-pick]')) row.dataset.awddPick = name;
		});

		const tokens = {
			postcode: res.postcode || '',
			councils: outside.join(', '),
			count: res.matches.length,
			council: '',
		};
		if (res.outcome === 'single' && match) {
			tokens.council = match.short || match.name;
			frag.querySelectorAll('[data-awdd-go]').forEach((b) => { b.dataset.awddGo = match.slug; });
			frag.querySelectorAll('[data-awdd-fill="name"]').forEach((el) => { el.textContent = match.name; });
			frag.querySelectorAll('img[data-awdd-fill="logo"]').forEach((img) => {
				img.src = match.logo;
				img.alt = match.name;
				img.style.setProperty('--mark-scale', String(Number(match.scale) > 0 ? Number(match.scale) : 1));
			});
		} else if (res.outcome === 'none') {
			tokens.council = outside[0] || '';
		}
		fillTokens(frag, tokens);

		// Outside the program: the postcode's own councils lead the select, and a
		// single one, or the one picked from a match, is selected outright.
		const select = frag.querySelector('[data-awdd-notify-select]');
		if (select) {
			const first = select.options[0];
			outside.slice().reverse().forEach((name) => first.after(new Option(name, name)));
			select.value = picked || (outside.length === 1 ? outside[0] : '');
		}

		result.appendChild(frag);

		if (res.outcome === 'invalid') {
			const msg = result.querySelector('[data-awdd-error]');
			if (msg && msg.id) {
				input.setAttribute('aria-invalid', 'true');
				input.setAttribute('aria-describedby', msg.id);
			}
		}

		const slot = result.querySelector('[data-awdd-notify-slot]');
		if (slot && wrapper) {
			// Whichever result held it last gives it up.
			if (notifyOwner && notifyOwner.slot !== slot) returnNotify(notifyOwner.slot);
			slot.appendChild(wrapper);
			notifyOwner = { slot, select: result.querySelector('[data-awdd-notify-select]'), postcode: res.postcode };
			syncNotify();
		}
	};

	form.addEventListener('submit', (e) => {
		e.preventDefault();
		const raw = input.value;
		loadData().then((data) => render(lookup(raw, data)));
	});

	root.addEventListener('focusin', () => { loadData(); }, { once: true });

	// A new search starts from a clean slate, as the static build clears on change.
	input.addEventListener('input', () => { if (last) clear(); });

	result.addEventListener('change', (e) => {
		if (e.target.matches('[data-awdd-notify-select]')) syncNotify();
	});

	result.addEventListener('click', (e) => {
		const goBtn = e.target.closest('[data-awdd-go]');
		if (goBtn) {
			go(bySlug(goBtn.dataset.awddGo));
			return;
		}
		if (e.target.closest('[data-awdd-retry]')) {
			clear();
			input.value = '';
			input.focus();
			return;
		}
		const pick = e.target.closest('[data-awdd-pick]');
		if (pick && last) {
			// "I am in" a council outside the program: the same form a miss gets.
			render({ ...last, outcome: 'none', matches: [] }, pick.dataset.awddPick);
		}
	});

	const list = root.querySelector('[data-awdd-lookup-list]');
	if (list) {
		list.addEventListener('change', () => {
			if (list.value) go(bySlug(list.value));
		});
	}

	// The link grid navigates by itself; the click only remembers the choice.
	root.querySelectorAll('[data-awdd-choose]').forEach((a) => {
		a.addEventListener('click', () => choose(a.dataset.awddChoose));
	});
}

/* ---------- Entry dialog ---------- */

function initEntry() {
	const wrap = document.getElementById('awdd-dialog-lookup');
	if (!wrap) return;

	// Closing it any way, the skip button included, is an answer for 30 days.
	wrap.addEventListener('awdd-dialog-close', dismiss);
	wrap.querySelectorAll('[data-awdd-lookup-skip]').forEach((b) => b.addEventListener('click', dismiss));

	// Front page only, after the page has painted, never over a known council.
	if (!document.body.classList.contains('home')) return;
	window.setTimeout(() => {
		// Never over a dialog or the menu drawer the visitor has already opened,
		// this dialog included. Both set body[data-dialog-open]; the menu toggle's
		// aria-expanded covers the drawer on its own.
		if (document.body.dataset.dialogOpen) return;
		if (document.querySelector('[data-awdd-menu-toggle][aria-expanded="true"]')) return;
		if (!current() && !isDismissed()) openDialog('lookup');
	}, ENTRY_DELAY);
}

export default function initLookups() {
	document.querySelectorAll('[data-awdd-lookup]').forEach(initLookup);

	// Gravity Forms swaps the notify form's markup after an AJAX submit. These are
	// jQuery events, so there is nothing to listen for without jQuery.
	if (window.jQuery) {
		window.jQuery(document).on('gform_post_render gform_confirmation_loaded', (e, formId) => {
			if (parseInt(formId, 10) === notifyId()) syncNotify();
		});
	}

	initEntry();
}
